<template>
  <div v-if="renderComponent">
    <div class="flex mb-6">
      <div v-if="isSearchable">
        <label for="query"
          class="block font-light font-sans leading-snug mb-1 subpixel-antialiased text-xl text-black w-full"
        >
          Search
        </label>
        <input id="query"
          class="bg-gray-300 border border-gray-800 rounded font-sans leading-snug max-w-2xl px-3 py-2 subpixel-antialiased text-gray-900 text-xl w-full"
          name="query"
          v-model="q"
        />
      </div>
      <div v-if="hasDateRange" :class="{ 'ml-4': isSearchable && hasDateRange }">
        <label
          class="block font-light font-sans leading-snug mb-1 subpixel-antialiased text-xl text-black w-full"
        >
          Date from
        </label>
        <datepicker
          class="font-sans leading-snug"
          v-model="d1"
        >            
        </datepicker>
      </div>
      <div v-if="hasDateRange" class="ml-4">
        <label
          class="block font-light font-sans leading-snug mb-1 subpixel-antialiased text-xl text-black w-full"
        >
          Date to
        </label>
        <datepicker
          class="font-sans leading-snug"
          v-model="d2"
        >            
        </datepicker>
      </div>
    </div>
    <div role="table">
      <doe-table-header
        class="hidden md:flex"
        :actions="actions"
        :columns="columns"
        :settings="settings"
        :isSelected="isAllSelected()"
        :isAnySelected="isAnySelected()"
        :isAllSelected="isAllSelected()"
        :sortKey="componentSortKey"
        :sortOrders="sortOrders"
        @input="updateAllSelectedItems($event)"
        @sortBy="sortBy($event)"
        @header:action="handleTableAction($event)"
        :hasActionBoxes="hasActionBoxes"
      />
      <component
        :is="`${dataset}Row`"
        v-for="(row, index) in filteredRows"
        :actions="actions"
        :columns="columns"
        :row="row"
        :isSelected="isSelected(row.uuid)"
        :key="index"
        @input="updateSelectedItems($event)"
        @row:action="handleTableAction($event)"
        :hasActionBoxes="hasActionBoxes"
      />
      <!-- <doe-pagination
        :current-page="currentPage"
        :items-per-page="itemsPerPage"
        :rows="generatedRows"
        @click="updateCurrentPage($event)"
      /> -->
    </div>
  </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker';
import dayjs from "dayjs";
import DoeTableHeader from "@/components/table/Header";
import StatementRow from "@/components/table/RowStatement.vue";
import UserRow from "@/components/table/RowUser.vue";
import ServiceRow from "@/components/table/RowService.vue";
import DoePagination from "@/components/table/Pagination.vue";

export default {
  name: "DoeTable",
  props: {
    dataset: {
      type: String,
      required: true
    },
    actions: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    settings: {
      type: Object,
      required: true
    },
    rows: {
      type: Array,
      required: true
    },
    hasActionBoxes: {
      type: Boolean
    },
    sortKey: {
      type: String
    },
    isSearchable: {
      type: Boolean
    },
    hasDateRange: {
      type: Boolean
    }
  },
  components: {
    Datepicker,
    DoeTableHeader,
    DoePagination,
    StatementRow,
    UserRow,
    ServiceRow
  },
  data() {
    var sortOrders = {};
    this.columns.forEach(column => {
      sortOrders[column.value] = -1;
    });
    return {
      renderComponent: true,
      q: "",
      d1: new Date().setFullYear(new Date().getFullYear()-1),
      d2: new Date(),
      sortOrders: sortOrders,
      // selectedItems: [],
      currentPage: 1,
      itemsPerPage: 150,
      globalChecked: false,
      componentSortKey: ""
    };
  },
  mounted() {
    this.componentSortKey = this.sortKey;
  },
  computed: {
    generatedRows() {
      let rows = [];
      rows = this.rows.map(item => {
        let row = this.columns.reduce((previous, current) => {
          previous[current.value] = current.cell(item);
          return previous;
        }, {});
        if (item.uuid) {
          row.uuid = item.uuid;
        }
        if (item.id) {
          row.id = item.id;
        }
        if (item.status) {
          row.status = item.status.value;
        }
        if (item.lastRouteViewed) {
          row.lastRouteViewed = item.lastRouteViewed;
        }
        if(item.archived){
          row.archived = item.archived;
        }
        if(item.visibleToSchoolTS) {
          row.visibleToSchoolTS = item.visibleToSchoolTS;
        }
        return row;
      });
      return rows;
    },
    filteredRows() {
      var sortKey = this.componentSortKey;
      var filterKey = this.q && this.q.toLowerCase();
      var order = this.sortOrders[sortKey] || 1;
      var rows = this.generatedRows;
      if (filterKey) {
        rows = rows.filter(row => {
          return Object.keys(row).some(key => {
            return (
              String(row[key].value)
                .toLowerCase()
                .indexOf(filterKey) > -1
            );
          });
        });
      }
      if(this.hasDateRange && this.sortKey==="archived") {
        rows = rows.filter(item => {
          const archivedTimestamp = dayjs(item.archived).valueOf();
          const startTimestamp = dayjs(this.d1).startOf('day').valueOf();
          const endTimestamp = dayjs(this.d2).endOf('day').valueOf();
          return archivedTimestamp >= startTimestamp && archivedTimestamp <= endTimestamp;
        })
      }

      if(this.hasDateRange && this.sortKey==="lastUpdatedDate"){
        Date.prototype.addHours = function(h) {
          this.setTime(this.getTime() + (h*60*60*1000));
          return this;
        }
        rows = rows.filter(item => {
          return item.lastUpdatedDate.value >= new Date(this.d1).addHours(10-24) && item.lastUpdatedDate.value <= new Date(this.d2).addHours(10)
        });
      }

      if (sortKey) {
        rows = rows.slice().sort((a, b) => {
          a = a[sortKey].value;
          b = b[sortKey].value;
          return (a === b ? 0 : a > b ? 1 : -1) * order;
        });
      }
      // return rows;
      return rows.slice(this.fromLocation, this.toLocation);
    },
    fromLocation() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    toLocation() {
      return this.fromLocation + this.itemsPerPage;
    }
  },
  methods: {
    handleTableAction($event) {
      this.$emit($event.action, $event);
    },
    sortBy($event) {
      this.componentSortKey = $event;
      this.sortOrders[$event] = this.sortOrders[$event] * -1;
    },
    updateAllSelectedItems($event) {
      this.$root.selectedItems = [];
      if ($event.addToSelected) {
        this.generatedRows.forEach(item => {

          this.$root.selectedItems.push(item.uuid);
        });
      }
      let that=this;
      that.renderComponent = false;
     
      that.$nextTick(function () {
        that.renderComponent = true;
      });
    },
    updateSelectedItems($event) {
      if ($event.addToSelected) {
        this.$root.selectedItems.push($event.identifier);
      } else {
        this.$root.selectedItems = this.$root.selectedItems.filter(
          item => item !== $event.identifier
        );
      }
      let that=this;
      that.renderComponent = false;
     
      that.$nextTick(function () {
        that.renderComponent = true;
      });
    },
    updateCurrentPage($event) {
      this.currentPage = $event.page;
      this.statementsPerPage = $event.statementsPerPage;
    },
    isSelected(uuid) {
      return this.$root.selectedItems.includes(uuid);
    },
    isAnySelected() {
      return this.$root.selectedItems.length >= 1;
    },
    isAllSelected() {
      return this.$root.selectedItems.length === this.rows.length;
    }
  }
};
</script>

<style lang="scss">
.vdp-datepicker input[type="text"]:read-only {
  @apply bg-gray-300 border border-gray-800 rounded font-sans leading-snug max-w-2xl px-3 py-2 subpixel-antialiased text-gray-900 text-xl w-full;
}
.vdp-datepicker__calendar .cell:not(.blank):not(.disabled).day:hover, 
.vdp-datepicker__calendar .cell:not(.blank):not(.disabled).month:hover, 
.vdp-datepicker__calendar .cell:not(.blank):not(.disabled).year:hover { 
  border: 1px solid #041e42 !important;
}
.vdp-datepicker__calendar .cell.selected {
  background: #041e42 !important;
  background-color: #041e42 !important;
  color: white !important;
}
.vdp-datepicker__calendar .cell.selected:hover {
  background: #041e42 !important;
  background-color: #041e42 !important;
}
</style>
